import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { fmtInt } from '@/lib/format'

const SIZES = [25, 50, 100]

interface PagerProps {
  offset: number
  pageSize: number
  total: number
  onOffset: (offset: number) => void
  onPageSize: (size: number) => void
  busy?: boolean
}

/**
 * List footer: "Показаны N–M из T, страница P из Q", a page-size chooser, and prev/next.
 * Shared across Заявки / Аудит / Пользователи so pagination reads the same everywhere
 * (design §6). Page numbers are 1-based for humans. An empty list has no footer: its empty
 * state ends with one action (§6.9), not «Показаны 0–0 из 0» and two disabled buttons.
 * On a phone (WP-15) it is one row: «Назад», «1 из 49», «Дальше» — 44 px buttons, no chooser.
 */
export function Pager({ offset, pageSize, total, onOffset, onPageSize, busy }: PagerProps) {
  if (total === 0) return null
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + pageSize, total)
  const page = Math.floor(offset / pageSize) + 1
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const prev = (
    <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - pageSize))}>
      Назад
    </Button>
  )
  const next = (
    <Button variant="secondary" size="sm" disabled={offset + pageSize >= total} onClick={() => onOffset(offset + pageSize)}>
      Дальше
    </Button>
  )

  return (
    <nav aria-label="Страницы списка" className="mt-4">
      <div className="flex items-center justify-between gap-3 md:hidden">
        {prev}
        <span className="tnum text-caption text-ink-2" aria-live="polite">
          {fmtInt(page)} из {fmtInt(pages)}
          {busy ? ', обновляю…' : ''}
        </span>
        {next}
      </div>
      <div className="hidden flex-wrap items-center justify-between gap-3 md:flex">
        <div className="flex flex-wrap items-center gap-3 text-caption text-ink-3">
          <span className="tnum">
            Показаны {fmtInt(from)}–{fmtInt(to)} из {fmtInt(total)}, страница {fmtInt(page)} из {fmtInt(pages)}
            {busy ? ', обновляю…' : ''}
          </span>
          <Select
            label="Строк на странице"
            hideLabel
            size="sm"
            className="w-20 print:hidden"
            value={String(pageSize)}
            options={SIZES.map((s) => ({ value: String(s), label: String(s) }))}
            onChange={(v) => {
              onPageSize(Number(v))
              onOffset(0)
            }}
          />
          <span aria-hidden className="print:hidden">
            на странице
          </span>
        </div>
        <div className="flex gap-2">
          {prev}
          {next}
        </div>
      </div>
    </nav>
  )
}
